import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import type { Observable, Subscription } from 'rxjs';
import { of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { NotificationsController } from '@recruitee/user-interface';
import { generateUuid } from '@recruitee/utils';

import type { Redactor, RedactorApp, RedactorConfig } from '../../redactor.types';
import type { Wrapper } from '../interfaces';
import { RedactorExtension } from '../redactor-extension';
import { dataURLtoFile, uploadFactory, validImageSizeFilter } from './upload-helpers';
import type { Base64Image, TrackableFile, TrackableFileType } from './upload.types';
import { WysiwygUploader } from './upload.types';

declare let $R: Redactor;

enum UploadErrorType {
  fileSize = 'fileSize',
  uploadFailed = 'uploadFailed',
}

export type UploadPluginError = Partial<Record<UploadErrorType, boolean>>;
export type UploadPluginErrors = { image?: UploadPluginError; other?: UploadPluginError } | null;

class RtUploadPlugin {
  constructor(private uploadExtension: RedactorUploadExtension) {}
  public init(app: RedactorApp) {
    const { cleaner } = app;
    cleaner.addConvertRules('rt-upload', ($wrapper: Wrapper) => {
      const images = <HTMLImageElement[]>$wrapper.find('img').nodes;
      const base64Images = images.filter(img => /^(data:image\/)/.test(img.src));

      if (!base64Images.length) return $wrapper;

      let uploadData: Base64Image[] = [];

      for (const image of base64Images) {
        const id = generateUuid();
        image.setAttribute('data-image', id);
        uploadData = [...uploadData, { id, base64: image.src }];
      }

      this.uploadExtension.handleImageUpload(uploadData, app);

      return $wrapper;
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class RedactorUploadExtension extends RedactorExtension {
  private readonly upload;

  private trackedFiles: TrackableFile[] = [];

  constructor(
    private uploader: WysiwygUploader,
    private notificationsCtrl: NotificationsController,
  ) {
    super();

    if (!$R.plugins['rt-upload']) {
      $R.add('plugin', 'rt-upload', new RtUploadPlugin(this));
    }

    this.upload = uploadFactory(uploader);
  }

  public canInstall(config: RedactorConfig): boolean {
    return Array.isArray(config.plugins) && config.plugins.includes('rt-upload');
  }

  public install(redactorConfig: RedactorConfig): RedactorConfig {
    this.enable();

    return {
      ...redactorConfig,
      imageUpload: (formData: FormData, fileList: FileList, event: Event, redactor: Redactor) => {
        const images = Array.from(fileList).filter(f => f.type.startsWith('image/'));
        return this.handleImageUpload(images, redactor.app);
      },
      fileUpload: this.handleFileUpload,
      callbacks: {
        ...(redactorConfig.callbacks || {}),
        file: {
          inserted: (element: HTMLAnchorElement) => this.initializeUpload(element),
        },
        image: {
          inserted: (element: any) =>
            this.applyFigureMinWidthFix(element) && this.initializeUpload(element),
        },
      },
    };
  }

  public handleFileUpload = (
    _formData: FormData,
    fileList: FileList,
    _event: Event,
    redactor: Redactor,
  ): { [key: string]: TrackableFile } => {
    const validFiles = Array.from(fileList).filter(validImageSizeFilter);
    if (fileList.length !== validFiles.length)
      this.broadcastPluginError('other', { [UploadErrorType.fileSize]: true }, redactor.app);

    const trackedFiles = this.mapToTrackableFiles(validFiles, 'other');
    this.trackedFiles = [...this.trackedFiles, ...trackedFiles];
    this.doUpload(trackedFiles, redactor.app, 'other');
    return this.mapToRedactorInsertionFormat(trackedFiles);
  };

  public handleImageUpload = (
    images: File[] | Base64Image[],
    redactor: RedactorApp,
  ): { [key: string]: TrackableFile } => {
    let validImages;

    if ((<File[]>images || []).every((file: File) => file.size)) {
      validImages = (<File[]>images).filter(validImageSizeFilter);
      if (images.length !== validImages.length)
        this.broadcastPluginError('image', { [UploadErrorType.fileSize]: true }, redactor);
    } else {
      validImages = images;
    }

    const trackedFiles = this.mapToTrackableFiles(validImages, 'image');
    this.trackedFiles = [...trackedFiles, ...this.trackedFiles];
    this.doUpload(trackedFiles, redactor, 'image');
    return this.mapToRedactorInsertionFormat(trackedFiles);
  };

  private broadcastPluginError(
    scope: TrackableFileType,
    errorType: Partial<Record<UploadErrorType, boolean>>,
    redactorApp: Redactor['app'],
  ): void {
    redactorApp.broadcast('uploadPluginError', {
      [scope]: errorType,
    });
  }

  private initializeUpload($element: any): void {
    const tracked = this.trackedFiles.find(({ id }) =>
      $element.find(`[data-file="${id}"], [data-image="${id}"]`),
    );

    if (tracked) {
      this.removeTrackedFile(tracked);
      $element.append('<div class="upload-cloak"></div>');
      $element.addClass('upload-in-progress');

      // eslint-disable-next-line default-case
      switch (tracked.type) {
        case 'image':
          $element.find('img').data('image', tracked.id);
          const $spinner = $R.dom(`
            <div class="progress-spinner-wrapper">
              <svg class="progress-spinner">
                <circle
                  class="progress-spinner__circle progress-spinner__circle--animate"
                  cx="10"
                  cy="10"
                  r="9"
                ></circle>
              </svg>
            </div>
          `);
          $element.find('.upload-cloak').append($spinner);
          break;
        case 'other':
          $element.find('a').data('file', tracked.id);
          break;
      }
    }
  }

  private applyFigureMinWidthFix($element: any): boolean {
    const $img = $element.find('img');
    const img: HTMLImageElement = $img.get();
    img.onload = () => {
      const style = { 'max-width': `${img.width}px`, width: '100%' };
      $element.css(style);
      $img.css(style);
    };
    return true;
  }

  private removeTrackedFile(trackedFile: TrackableFile): void {
    this.trackedFiles = this.trackedFiles.filter(({ id }) => id !== trackedFile.id);
  }

  private uploadRedactorAsset(files: TrackableFile[]): Observable<TrackableFile> {
    return of(...files).pipe(mergeMap(file => this.upload(file)));
  }

  private mapToTrackableFiles(
    files: File[] | Base64Image[],
    type: TrackableFileType,
  ): TrackableFile[] {
    return (<Array<File | Base64Image>>files).map(fileOrData => {
      if (fileOrData instanceof File) {
        const file = fileOrData;
        return {
          id: generateUuid(),
          type,
          url: URL.createObjectURL(file),
          file,
        };
      } else {
        const base64Data = fileOrData;
        return {
          id: base64Data.id,
          type,
          url: base64Data.base64,
          file: dataURLtoFile(base64Data.base64, base64Data.id),
        };
      }
    });
  }

  private mapToRedactorInsertionFormat(files: TrackableFile[]): { [key: string]: TrackableFile } {
    return [...files.reverse()].reduce(
      (acc, f) => ({
        [f.file.name]: {
          ...f,
          name: f.name || f.file.name,
        },
        ...acc,
      }),
      {},
    );
  }

  private replacePendingFileWithUploaded(
    { id, url, type }: TrackableFile,
    redactor: RedactorApp,
  ): void {
    const $editor = redactor.container.getElement();

    switch (type) {
      case 'image':
        $editor
          .find(`[data-image="${id}"]`)
          .attr('src', url)
          .parent()
          .removeClass('upload-in-progress')
          .find('.upload-cloak')
          .remove();
        break;

      case 'other':
        $editor
          .find(`[data-file="${id}"]`)
          .attr('href', url)
          .removeClass('upload-in-progress')
          .find('.upload-cloak')
          .remove();
        break;

      default:
        return;
    }
  }

  private doUpload(
    trackedFiles: TrackableFile[],
    redactor: RedactorApp,
    type: 'image' | 'other',
  ): Subscription {
    return this.uploadRedactorAsset(trackedFiles).subscribe(file => {
      redactor.broadcast('uploadPluginDone', type);
      this.replacePendingFileWithUploaded(file, redactor);
      this.removeTrackedFile(file);
      if (file.error && file.error instanceof HttpErrorResponse) {
        const { error: errorBody } = file.error;
        this.notificationsCtrl.error(errorBody.error[0]);
      }
    });
  }
}
